import React, { Fragment } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Network, DollarSign, Megaphone, Users, UserX, Globe, AlertTriangle, Sparkles, Clock } from 'lucide-react';
import { Badge } from '../components/Badge';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
const dimensions = [{
  id: 'eco',
  name: 'Ekonomi',
  score: 88,
  icon: DollarSign,
  color: 'text-red-500',
  bg: 'bg-red-500',
  border: 'border-red-500/30',
  active: 5
}, {
  id: 'pol',
  name: 'Propaganda Politik',
  score: 72,
  icon: Megaphone,
  color: 'text-amber-500',
  bg: 'bg-amber-500',
  border: 'border-amber-500/30',
  active: 3
}, {
  id: 'soc',
  name: 'Mobilisasi Sosial',
  score: 65,
  icon: Users,
  color: 'text-amber-500',
  bg: 'bg-amber-500',
  border: 'border-amber-500/30',
  active: 2
}, {
  id: 'inf',
  name: 'Influencer Attack',
  score: 78,
  icon: UserX,
  color: 'text-red-500',
  bg: 'bg-red-500',
  border: 'border-red-500/30',
  active: 4
}, {
  id: 'for',
  name: 'Pengaruh Asing',
  score: 54,
  icon: Globe,
  color: 'text-amber-500',
  bg: 'bg-amber-500',
  border: 'border-amber-500/30',
  active: 1
}];
const dimLabels = ['Ekonomi', 'Propaganda', 'Mobilisasi', 'Influencer', 'Asing'];
const matrix = [[100, 84, 71, 62, 48], [84, 100, 67, 79, 58], [71, 67, 100, 55, 71], [62, 79, 55, 100, 66], [48, 58, 71, 66, 100]];
const getCellTone = (val: number, isDiag: boolean) => {
  if (isDiag) return 'bg-muted/40 text-muted-foreground';
  if (val >= 75) return 'bg-red-500 text-white';
  if (val >= 55) return 'bg-amber-500 text-white';
  return 'bg-slate-300 text-slate-700';
};
const patterns = [{
  id: 'A',
  name: 'Krisis Pangan + Propaganda Politik',
  vectors: ['Inflasi Beras', 'Hoaks Bansos', 'Demo Buruh', '#TolakKebijakanX'],
  severity: 'CRITICAL',
  likelihood: 87,
  window: '24-48 jam',
  desc: 'Kombinasi tekanan ekonomi dan amplifikasi narasi politik berpotensi memicu mobilisasi massa di Jawa & Sumatera.'
}, {
  id: 'B',
  name: 'Cyber Influence Campaign',
  vectors: ['Foreign Server', 'Bot Farm', 'Deepfake Audio', 'Hostile Hashtag'],
  severity: 'HIGH',
  likelihood: 72,
  window: '48-72 jam',
  desc: 'Kampanye terkoordinasi dari aktor asing dengan amplifikasi domestik via 1.247 akun terindikasi bot.'
}, {
  id: 'C',
  name: 'Pre-Visit Destabilization',
  vectors: ['Local Trend Manipulation', 'Separatist Narrative', 'Logistic Disinfo', 'VIP Threat'],
  severity: 'CRITICAL',
  likelihood: 64,
  window: 'Hingga 25 Mei',
  desc: 'Sinkronisasi narasi destabilisasi menjelang kunjungan Presiden ke Papua — 4 vektor aktif.'
}];
const trendData = [{
  day: 'Sen',
  Ekonomi: 60,
  Propaganda: 45,
  Mobilisasi: 30,
  Influencer: 50,
  Asing: 25
}, {
  day: 'Sel',
  Ekonomi: 64,
  Propaganda: 50,
  Mobilisasi: 35,
  Influencer: 55,
  Asing: 30
}, {
  day: 'Rab',
  Ekonomi: 72,
  Propaganda: 58,
  Mobilisasi: 42,
  Influencer: 62,
  Asing: 35
}, {
  day: 'Kam',
  Ekonomi: 78,
  Propaganda: 64,
  Mobilisasi: 50,
  Influencer: 70,
  Asing: 42
}, {
  day: 'Jum',
  Ekonomi: 82,
  Propaganda: 68,
  Mobilisasi: 58,
  Influencer: 74,
  Asing: 48
}, {
  day: 'Sab',
  Ekonomi: 86,
  Propaganda: 70,
  Mobilisasi: 62,
  Influencer: 76,
  Asing: 52
}, {
  day: 'Min',
  Ekonomi: 88,
  Propaganda: 72,
  Mobilisasi: 65,
  Influencer: 78,
  Asing: 54
}];
const severityClass: Record<string, string> = {
  CRITICAL: 'bg-red-500/10 text-red-500 border-red-500/30',
  HIGH: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  MEDIUM: 'bg-blue-500/10 text-blue-500 border-blue-500/30'
};
export function HybridThreats() {
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6 pb-10" data-id="element-2101">
      <PageHeader group="Ancaman & Proteksi" title="Hybrid Threat Correlation Engine" description="Korelasi multi-dimensi ancaman: ekonomi, propaganda, mobilisasi sosial, influencer attack, dan pengaruh asing dalam satu pola terpadu." icon={Network} data-id="element-2102" />

      {/* ROW 1: Dimensions */}
      <div className="grid grid-cols-12 gap-4" data-id="element-2103">
        {dimensions.map(d => <Card key={d.id} className={`col-span-12 sm:col-span-6 lg:col-span-2 bg-card border ${d.border}`} data-id="element-2104">
            <CardContent className="p-4" data-id="element-2105">
              <div className="flex items-start justify-between mb-3" data-id="element-2106">
                <d.icon className={`size-5 ${d.color}`} data-id="element-2107" />
                <Badge variant="outline" className="font-mono text-[9px] h-4" data-id="element-2108">
                  {d.active} aktif
                </Badge>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-id="element-2109">
                {d.name}
              </p>
              <p className={`font-mono text-2xl font-bold mt-1 ${d.color}`} data-id="element-2110">
                {d.score}
              </p>
              <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden mt-2" data-id="element-2111">
                <div className={`h-full ${d.bg}`} style={{
              width: `${d.score}%`
            }} data-id="element-2112" />
              </div>
            </CardContent>
          </Card>)}
      </div>

      {/* ROW 2: Correlation Matrix */}
      <Card className="bg-card border-border/50" data-id="element-2113">
        <CardHeader className="pb-3 pt-4 px-5 border-b border-border/50" data-id="element-2114">
          <CardTitle className="font-mono text-sm uppercase tracking-wider flex items-center gap-2" data-id="element-2115">
            <AlertTriangle className="size-4 text-amber-500" data-id="element-2116" />
            Correlation Matrix
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5" data-id="element-2117">
          <div className="overflow-x-auto" data-id="element-2118">
            <div className="inline-grid gap-1" style={{
            gridTemplateColumns: 'auto repeat(5, minmax(80px, 1fr))'
          }} data-id="element-2119">
              <div data-id="element-2120" />
              {dimLabels.map(l => <div key={l} className="font-mono text-[10px] uppercase text-muted-foreground text-center pb-2" data-id="element-2121">
                  {l}
                </div>)}
              {matrix.map((row, i) => <Fragment key={i} data-id="element-2122">
                  <div className="font-mono text-[10px] uppercase text-muted-foreground pr-2 flex items-center justify-end" data-id="element-2123">
                    {dimLabels[i]}
                  </div>
                  {row.map((val, j) => <div key={j} className={`aspect-square min-h-[44px] flex items-center justify-center rounded font-mono text-[11px] font-bold ${getCellTone(val, i === j)}`} data-id="element-2124">
                      {(val / 100).toFixed(2)}
                    </div>)}
                </Fragment>)}
            </div>
          </div>

          <div className="mt-5 bg-primary/5 border border-primary/20 rounded-md p-4" data-id="element-2125">
            <div className="flex items-start gap-2.5" data-id="element-2126">
              <Sparkles className="size-4 text-primary shrink-0 mt-0.5" data-id="element-2127" />
              <div data-id="element-2128">
                <p className="font-mono text-[10px] font-bold text-primary uppercase tracking-wider mb-1" data-id="element-2129">
                  Interpretasi AI
                </p>
                <p className="text-xs italic text-foreground/80 leading-relaxed" data-id="element-2130">
                  Ditemukan 3 korelasi tinggi:{' '}
                  <strong data-id="element-2131">Ekonomi × Propaganda (0.84)</strong>,{' '}
                  <strong data-id="element-2132">Propaganda × Influencer (0.79)</strong>, dan{' '}
                  <strong data-id="element-2133">Mobilisasi × Pengaruh Asing (0.71)</strong>. Pola
                  hybrid attack TERKONFIRMASI.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ROW 3: Detected Hybrid Patterns */}
      <div data-id="element-2134">
        <h3 className="font-mono text-sm uppercase tracking-wider text-foreground mb-3 flex items-center gap-2" data-id="element-2135">
          <AlertTriangle className="size-4 text-red-500" data-id="element-2136" />
          Detected Hybrid Patterns
        </h3>
        <div className="grid grid-cols-12 gap-4" data-id="element-2137">
          {patterns.map(p => <Card key={p.id} className="col-span-12 md:col-span-4 bg-card border-border/50 flex flex-col" data-id="element-2138">
              <CardContent className="p-5 flex flex-col flex-1" data-id="element-2139">
                <div className="flex items-center justify-between mb-2" data-id="element-2140">
                  <Badge variant="outline" className={`font-mono text-[9px] h-5 border ${severityClass[p.severity]}`} data-id="element-2141">
                    {p.severity}
                  </Badge>
                  <span className="font-mono text-[10px] text-muted-foreground flex items-center gap-1" data-id="element-2142">
                    <Clock className="size-3" data-id="element-2143" /> {p.window}
                  </span>
                </div>
                <h4 className="text-sm font-bold text-foreground mb-2" data-id="element-2144">
                  {p.name}
                </h4>
                <p className="text-[11px] text-muted-foreground leading-relaxed mb-3" data-id="element-2145">
                  {p.desc}
                </p>

                <div className="mb-3" data-id="element-2146">
                  <p className="font-mono text-[9px] uppercase text-muted-foreground mb-1.5" data-id="element-2147">
                    Vektor
                  </p>
                  <div className="flex flex-wrap gap-1" data-id="element-2148">
                    {p.vectors.map(v => <span key={v} className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-muted/50 border border-border/50 text-foreground" data-id="element-2149">
                        {v}
                      </span>)}
                  </div>
                </div>

                <div className="mt-auto" data-id="element-2150">
                  <div className="flex items-center justify-between mb-1" data-id="element-2151">
                    <span className="font-mono text-[9px] uppercase text-muted-foreground" data-id="element-2152">
                      Likelihood
                    </span>
                    <span className="font-mono text-xs font-bold text-foreground" data-id="element-2153">
                      {p.likelihood}%
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden" data-id="element-2154">
                    <div className={`h-full ${p.likelihood >= 80 ? 'bg-red-500' : p.likelihood >= 60 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{
                  width: `${p.likelihood}%`
                }} data-id="element-2155" />
                  </div>
                  <div className="flex items-center gap-1 mt-3" data-id="element-2156">
                    {p.vectors.map((_, i) => <span key={i} className="flex-1 h-1 rounded-full bg-primary/40" data-id="element-2157" />)}
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>

      {/* ROW 4: Cross-Dimension Timeline */}
      <Card className="bg-card border-border/50" data-id="element-2158">
        <CardHeader className="pb-3 pt-4 px-5 border-b border-border/50" data-id="element-2159">
          <CardTitle className="font-mono text-sm uppercase tracking-wider" data-id="element-2160">
            Eskalasi Multi-Dimensi 7 Hari Terakhir
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5" data-id="element-2161">
          <div className="h-[280px]" data-id="element-2162">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2163">
              <AreaChart data={trendData} margin={{
              top: 10,
              right: 10,
              left: -10,
              bottom: 0
            }} data-id="element-2164">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} data-id="element-2165" />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} axisLine={false} data-id="element-2166" />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} axisLine={false} data-id="element-2167" />
                <Tooltip contentStyle={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                fontSize: 11
              }} data-id="element-2168" />
                <Area type="monotone" dataKey="Ekonomi" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} data-id="element-2169" />
                <Area type="monotone" dataKey="Propaganda" stackId="1" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} data-id="element-2170" />
                <Area type="monotone" dataKey="Mobilisasi" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} data-id="element-2171" />
                <Area type="monotone" dataKey="Influencer" stackId="1" stroke="#a855f7" fill="#a855f7" fillOpacity={0.6} data-id="element-2172" />
                <Area type="monotone" dataKey="Asing" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} data-id="element-2173" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-4 space-y-1.5" data-id="element-2174">
            <li className="flex items-start gap-2 text-xs text-foreground/90" data-id="element-2175">
              <span className="mt-1.5 size-1.5 rounded-full bg-red-500 shrink-0" data-id="element-2176" />
              Dimensi Ekonomi naik +47% sejak Senin — pemicu utama keseluruhan
              eskalasi.
            </li>
            <li className="flex items-start gap-2 text-xs text-foreground/90" data-id="element-2177">
              <span className="mt-1.5 size-1.5 rounded-full bg-amber-500 shrink-0" data-id="element-2178" />
              Propaganda Politik mengikuti Ekonomi dengan jeda 12-18 jam
              (delayed correlation).
            </li>
            <li className="flex items-start gap-2 text-xs text-foreground/90" data-id="element-2179">
              <span className="mt-1.5 size-1.5 rounded-full bg-primary shrink-0" data-id="element-2180" />
              Aktivitas Pengaruh Asing meningkat paralel — pola koordinasi
              tingkat doktrinal.
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>;
}